import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Contact, Globe, Instagram, Menu, Send, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LLink, LNavLink, switchLangPath, useLang } from '../../lib/lang';
import { Container } from './ui';

export const CONTACT_EMAIL = 'tjb.ai@yandex.ru';

// Соцсети зависят от языка: для русской версии — ВКонтакте и Telegram, для английской — Telegram и Instagram.
const SOCIAL = {
  ru: [
    { name: 'VKontakte', url: 'https://vk.com/tjb_ai', Icon: Contact, hover: 'hover:text-blue-600' },
    { name: 'Telegram', url: 'https://t.me/teethjawsbones', Icon: Send, hover: 'hover:text-blue-400' },
  ],
  en: [
    { name: 'Telegram', url: 'https://t.me/teethjawsbones', Icon: Send, hover: 'hover:text-blue-400' },
    { name: 'Instagram', url: 'https://instagram.com/tjb_ai', Icon: Instagram, hover: 'hover:text-pink-600' },
  ],
};

export const SocialLinks = ({ size = 24, withLabels = false, className = '' }) => {
  const { lang } = useLang();
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {SOCIAL[lang].map(({ name, url, Icon, hover }) => (
        <a key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={name}
          className={`flex items-center gap-2 text-gray-700 transition-colors ${hover}`}>
          <Icon size={size} />{withLabels && <span>{name}</span>}
        </a>
      ))}
    </div>
  );
};

const navCls = ({ isActive }) =>
  `text-lg hover:text-[#C41E3A] transition-colors ${isActive ? 'text-[#C41E3A] font-bold' : ''}`;

export const LanguageSwitch = ({ onDone }) => {
  const { lang } = useLang();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  return (
    <button onClick={() => { navigate(switchLangPath(pathname, search, hash)); if (onDone) onDone(); }}
      className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
      aria-label={lang === 'ru' ? 'Switch to English' : 'Переключить на русский'}>
      <Globe size={20} />{lang === 'ru' ? 'EN' : 'RU'}
    </button>
  );
};

export const SiteHeader = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasPetrovich = !!(user && (user.is_admin || user.petrovich_access));
  const close = () => setOpen(false);

  const items = (
    <>
      <LNavLink to="/catalog" className={navCls} onClick={close}>{t('pl.nav.tools')}</LNavLink>
      <LNavLink to="/webinars" className={navCls} onClick={close}>{t('pl.nav.courses')}</LNavLink>
      <LNavLink to="/news" className={navCls} onClick={close}>{t('pl.nav.news')}</LNavLink>
      {hasPetrovich ? <LNavLink to="/metrica" className={navCls} onClick={close}>{t('pl.nav.petrovich')}</LNavLink> : null}
      <LNavLink to="/contacts" className={navCls} onClick={close}>{t('pl.nav.contacts')}</LNavLink>
    </>
  );

  return (
    <header className="sticky top-0 z-50 bg-[#F5F1E8] border-b-2 border-black tech-lines">
      <Container>
        <div className="flex items-center justify-between py-3">
          <LLink to="/" className="flex items-center gap-3" onClick={close}>
            <img src="/photo_2025-10-22_17-14-57.jpg" alt="TJB" className="h-12 w-12 object-contain" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>TJB</span>
          </LLink>
          <nav className="hidden lg:flex items-center gap-7">{items}</nav>
          <div className="hidden lg:flex items-center gap-5">
            <LanguageSwitch />
            {user ? (
              <LLink to="/cabinet" className="engraved-button engraved-button-red">{t('pl.nav.cabinet')}</LLink>
            ) : (
              <>
                <LLink to="/login" className="text-lg hover:text-[#C41E3A]">{t('pl.nav.login')}</LLink>
                <LLink to="/register" className="engraved-button engraved-button-red">{t('pl.nav.register')}</LLink>
              </>
            )}
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen((v) => !v)} aria-label={t('pl.nav.menu')} aria-expanded={open}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden pb-4 flex flex-col gap-3 border-t border-black/20 pt-4">
            {items}
            <LanguageSwitch onDone={close} />
            {user ? (
              <LLink to="/cabinet" className="engraved-button engraved-button-red text-center" onClick={close}>{t('pl.nav.cabinet')}</LLink>
            ) : (
              <>
                <LLink to="/login" className="text-lg" onClick={close}>{t('pl.nav.login')}</LLink>
                <LLink to="/register" className="engraved-button engraved-button-red text-center" onClick={close}>{t('pl.nav.register')}</LLink>
              </>
            )}
          </div>
        )}
      </Container>
    </header>
  );
};

export const SiteFooter = () => {
  const { t } = useTranslation();
  return (
    <footer className="border-t-2 border-black bg-[#E8E3D5] mt-16">
      <Container className="py-8 grid gap-6 md:grid-cols-4 text-sm">
        <div>
          <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>TJB</p>
          <p className="text-gray-700">{t('pl.footer.tagline')}</p>
          <p className="text-gray-500 mt-2">{t('pl.footer.copyright')}</p>
        </div>
        <div className="flex flex-col gap-1">
          <LLink to="/catalog" className="hover:text-[#C41E3A]">{t('pl.nav.tools')}</LLink>
          <LLink to="/webinars" className="hover:text-[#C41E3A]">{t('pl.nav.courses')}</LLink>
          <LLink to="/news" className="hover:text-[#C41E3A]">{t('pl.nav.news')}</LLink>
          <LLink to="/privacy-policy" className="hover:text-[#C41E3A]">{t('pl.footer.privacy')}</LLink>
        </div>
        <div>
          <p className="mb-1">{t('pl.footer.contactUs')}</p>
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C41E3A] font-bold">{CONTACT_EMAIL}</a>
        </div>
        <div>
          <p className="mb-2">{t('pl.footer.social')}</p>
          <SocialLinks />
        </div>
      </Container>
    </footer>
  );
};

export const SiteLayout = ({ children }) => (
  <div className="min-h-screen paper-texture flex flex-col">
    <SiteHeader />
    <main className="flex-1">{children}</main>
    <SiteFooter />
  </div>
);

export const useDocumentTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = `${title} — TJB`;
  }, [title]);
};
